import { useState, useEffect } from "react";
import { TextField, IconButton, InputAdornment } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const clamp = (value, min, max) => {
  let result = value;
  if (typeof min === "number" && result < min) result = min;
  if (typeof max === "number" && result > max) result = max;
  return result;
};

/**
 * Keyboard-typable bounded number field with +/- buttons. Stays agnostic of
 * any cross-field business rule — pass `error`/`helperText` for that.
 */
export const QuantityStepper = ({
  value,
  onChange,
  min,
  max,
  label,
  error,
  helperText,
  disabled = false,
  size = "small"
}) => {
  const [text, setText] = useState(String(value));

  useEffect(() => {
    setText(String(value));
  }, [value]);

  const commit = raw => {
    const parsed = parseInt(raw, 10);
    if (isNaN(parsed)) {
      setText(String(value));
      return;
    }
    const clamped = clamp(parsed, min, max);
    setText(String(clamped));
    onChange?.(clamped);
  };

  const handleTextChange = event => {
    const raw = event.target.value;
    setText(raw);
    const parsed = parseInt(raw, 10);
    if (!isNaN(parsed)) {
      onChange?.(parsed);
    }
  };

  const canDecrement = typeof min !== "number" || value > min;
  const canIncrement = typeof max !== "number" || value < max;

  return (
    <TextField
      type="number"
      label={label}
      value={text}
      size={size}
      error={error}
      helperText={helperText}
      disabled={disabled}
      onChange={handleTextChange}
      onBlur={event => commit(event.target.value)}
      onKeyDown={event => {
        if (event.key === "Enter") commit(event.target.value);
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              size="small"
              disabled={disabled || !canDecrement}
              onClick={() => commit(String(value - 1))}
              aria-label="Disminuir"
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              size="small"
              disabled={disabled || !canIncrement}
              onClick={() => commit(String(value + 1))}
              aria-label="Aumentar"
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        )
      }}
    />
  );
};

export default QuantityStepper;
